import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { 
  BarChart3, 
  Search, 
  Briefcase, 
  Menu,
  X,
  Globe,
  Flame,
  FileSearch,
  Activity,
  ChevronDown,
  DollarSign,
  ArrowUpCircle,
  Newspaper,
  TrendingUp,
  RefreshCw,
  Landmark,
  AlertTriangle,
  Bell,
  Layers,
  Wallet
} from "lucide-react";

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const menuRef = useRef(null);

  // Force a full page reload — refetches ALL data (MSN prices, charts, news, everything).
  // Cache-busts the HTML doc to guarantee the newest bundle is pulled.
  const handleHardRefresh = () => {
    setRefreshing(true);
    // Tiny delay so the spin animation is visible before reload
    setTimeout(() => {
      window.location.reload();
    }, 150);
  };

  const navLinks = [
    { path: "/", label: "Market Overview", icon: Globe },
    { path: "/stock", label: "Stock Analysis", icon: BarChart3 },
    { path: "/political-trades", label: "Political Trades", icon: Landmark },
    { path: "/search-attention", label: "Search Attention", icon: Activity },
    { path: "/portfolio", label: "Portfolio Designer", icon: Briefcase },
    { path: "/daily-rebalancing", label: "Rebalancing", icon: RefreshCw },
    { path: "/early-warnings", label: "Early Warnings", icon: AlertTriangle },
    { path: "/sector-analysis", label: "Sector Analysis", icon: Layers },
    { path: "/all-stocks", label: "All Stocks", icon: Layers },
    { path: "/all-etfs", label: "All ETFs", icon: Wallet },
    { path: "/market-indicators", label: "Market Indicators", icon: Activity },
    { path: "/payday-anomaly", label: "Payday Anomaly", icon: DollarSign },
    { path: "/money-flow", label: "Money Flow", icon: ArrowUpCircle },
    { path: "/global-markets", label: "Global Markets", icon: Globe },
    { path: "/news", label: "Today's News", icon: Newspaper },
    { path: "/scanner", label: "5:1 R/R Scanner", icon: Search },
    { path: "/options-generator", label: "Options Generator", icon: Flame },
    { path: "/options-scanner-log", label: "Options Scanner Log", icon: FileSearch },
    { path: "/treasury", label: "10Y Treasury", icon: TrendingUp },
    { path: "/portfolio-analyzer", label: "Portfolio Analyzer", icon: FileSearch },
    { path: "/alerts", label: "Alerts Manager", icon: Bell },
  ];

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/" || location.pathname === "/market";
    }
    return location.pathname.startsWith(path);
  };

  // Get current page name for display
  const getCurrentPageName = () => {
    const currentLink = navLinks.find(link => isActive(link.path));
    return currentLink ? currentLink.label : "Menu";
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group" target="_blank" rel="noopener noreferrer">
            <img 
              src="https://customer-assets.emergentagent.com/job_6ab71d84-1c0a-4dfc-96a7-091e2ccdbc04/artifacts/n9h4u4r1_Stock%20Pimp%20Logo.png" 
              alt="Stock Pimp AI Logo" 
              className="w-8 h-8 rounded-lg"
            />
            <span className="text-lg font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
              StockPimp AI
            </span>
          </a>

          {/* Hamburger Menu + Refresh Button */}
          <div className="flex items-center gap-2">
            {/* Universal Refresh Button — forces full page reload on every route */}
            <button
              onClick={handleHardRefresh}
              disabled={refreshing}
              title="Refresh page — pull fresh data for every source"
              aria-label="Refresh page"
              data-testid="navbar-refresh-btn"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-300 bg-purple-900/30 hover:bg-purple-800/50 hover:text-purple-200 rounded-lg border border-purple-800/50 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-2 px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-all duration-200"
              data-testid="hamburger-menu-btn"
            >
              {/* Three horizontal lines (hamburger icon) */}
              {menuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
              <span className="hidden sm:inline text-sm font-medium">{getCurrentPageName()}</span>
              <ChevronDown className={`w-4 h-4 hidden sm:block transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-64 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-y-auto overflow-x-hidden z-50 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent"
                style={{ maxHeight: "calc(100vh - 5rem)" }}
                data-testid="nav-menu-dropdown"
              >
                <div className="py-2">
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    const active = isActive(link.path);
                    return (
                      <a
                        key={link.path}
                        href={link.path}
                        target="_blank" rel="noopener noreferrer"
                        onClick={() => setMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all ${
                          active
                            ? "bg-purple-600/20 text-purple-400 border-l-2 border-purple-500"
                            : "text-slate-300 hover:text-white hover:bg-slate-800"
                        }`}
                        data-testid={`nav-${link.path === "/" ? "market" : link.path.slice(1)}`}
                      >
                        <Icon className={`w-5 h-5 ${active ? "text-purple-400" : "text-slate-400"}`} />
                        <span>{link.label}</span>
                        {active && (
                          <span className="ml-auto text-xs text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded">
                            Current
                          </span>
                        )}
                      </a>
                    );
                  })}
                </div>
                
                {/* Footer */}
                <div className="px-4 py-2 bg-slate-800/50 border-t border-slate-700">
                  <p className="text-xs text-slate-500 text-center">
                    Powered by Finnhub & Yahoo Finance
                  </p>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
